// Remote presentation (§20.8). The render timeline runs `interpDelay` ticks behind the estimated
// server tick; beans and entities are interpolated between the two snapshots that bracket it, and
// extrapolated for at most 6 ticks past the newest one. DOM-free.
import { DT } from '@shared/constants';
import { angleDiff, approach, clamp, lerp, wrapAngle } from '@shared/math';
import type { SnapBean, SnapEnt } from '@shared/protocol/schema';
import { TICK_MS } from '../core/time';
import { createBracket, type Bracket, type SnapshotBuffer } from '../net/SnapshotBuffer';
import { beanViewFromSnap, entityViewFromSnap, resizeEntityViews, type BeanView, type EntityView } from './FrameView';

export const INTERPOLATION = {
  minDelayTicks: 4,
  maxDelayTicks: 9,
  /** Delay ticks added per tick of jitter. */
  jitterGain: 2,
  /** Fastest change of the applied delay, ticks per second. */
  adaptTicksPerSec: 0.5,
  maxExtrapolateTicks: 6,
  /** A move longer than this between two snapshots is a teleport: snap at B instead of lerping. */
  teleportDist: 3,
} as const;

/** `clamp(4 + 2 × jitterMs / TICK_MS, 4, 9)` ticks. */
export function targetInterpDelay(jitterMs: number): number {
  return clamp(INTERPOLATION.minDelayTicks + (INTERPOLATION.jitterGain * jitterMs) / TICK_MS, INTERPOLATION.minDelayTicks, INTERPOLATION.maxDelayTicks);
}

function teleported(ax: number, ay: number, bx: number, by: number): boolean {
  return Math.hypot(bx - ax, by - ay) > INTERPOLATION.teleportDist;
}

/**
 * Sim fields of an active bean between records `a` and `b` (b null: extrapolate from `a`).
 * `t` is the lerp fraction and `since` the ticks elapsed after `a` (already capped when
 * extrapolating). Discrete fields come from `a`; `st` advances with render time.
 */
export function sampleBean(out: BeanView, a: SnapBean, b: SnapBean | null, t: number, since: number): void {
  beanViewFromSnap(out, a);
  out.st = a.st + since;
  // Cooldowns run down with render time, like `st`.
  out.ledgeCd = Math.max(0, a.cdLedge - since);
  if (!b) {
    out.x = a.x + a.vx * since * DT;
    out.y = a.y + a.vy * since * DT;
    return;
  }
  if (teleported(a.x, a.y, b.x, b.y)) return;
  out.x = lerp(a.x, b.x, t);
  out.y = lerp(a.y, b.y, t);
  out.z = lerp(a.z, b.z, t);
  out.vx = lerp(a.vx, b.vx, t);
  out.vy = lerp(a.vy, b.vy, t);
  out.vz = lerp(a.vz, b.vz, t);
  out.facing = wrapAngle(a.facing + angleDiff(a.facing, b.facing) * t);
  out.dizzy = lerp(a.dizzy, b.dizzy, t);
}

/** Entity counterpart of `sampleBean`; an entity missing from `b` extrapolates from `a`. */
export function sampleEntity(out: EntityView, a: SnapEnt, b: SnapEnt | null, t: number, since: number): void {
  entityViewFromSnap(out, a);
  out.t = a.t + since;
  if (!b) {
    const s = Math.min(since, INTERPOLATION.maxExtrapolateTicks) * DT;
    out.x = a.x + a.vx * s;
    out.y = a.y + a.vy * s;
    return;
  }
  if (teleported(a.x, a.y, b.x, b.y)) return;
  out.x = lerp(a.x, b.x, t);
  out.y = lerp(a.y, b.y, t);
  out.z = lerp(a.z, b.z, t);
  out.vx = lerp(a.vx, b.vx, t);
  out.vy = lerp(a.vy, b.vy, t);
  out.vz = lerp(a.vz, b.vz, t);
}

function findEnt(ents: readonly SnapEnt[], id: number): SnapEnt | null {
  for (const e of ents) if (e.id === id) return e;
  return null;
}

export class Interpolation {
  /** Applied interpolation delay, ticks. */
  delay: number = INTERPOLATION.minDelayTicks;
  /** Fractional room tick that remotes are drawn at. */
  renderTick = 0;
  /** The bracket of the last `prepare()`. */
  readonly bracket: Bracket = createBracket();
  private started = false;

  /**
   * Advances the render timeline for this frame and returns it: `delay` ticks behind the estimated
   * server tick (§20.8), but never closer than `minBufferTicks` to `streamTick`, the room tick of the
   * snapshots arriving now (`ClockSync.arrivalTick`). On a long link the second bound wins, so
   * remotes are interpolated rather than dead-reckoned. The delay moves toward its jitter target at
   * most 0.5 ticks per second, and the timeline never runs backwards until `reset()`.
   */
  update(estServerTick: number, jitterMs: number, dt: number, streamTick = Number.POSITIVE_INFINITY, minBufferTicks = 0): number {
    const target = targetInterpDelay(jitterMs);
    if (!this.started) {
      this.started = true;
      this.delay = target;
      this.renderTick = Math.min(estServerTick - target, streamTick - minBufferTicks);
      return this.renderTick;
    }
    this.delay = approach(this.delay, target, INTERPOLATION.adaptTicksPerSec * dt);
    this.renderTick = Math.max(this.renderTick, Math.min(estServerTick - this.delay, streamTick - minBufferTicks));
    return this.renderTick;
  }

  /** Restarts the timeline at the next `update()` (new round, hard resync). */
  reset(): void {
    this.started = false;
  }

  /** Brackets the current render tick; call once per frame before sampling. */
  prepare(buf: SnapshotBuffer): Bracket {
    return buf.bracket(this.renderTick, this.bracket);
  }

  /** Ticks elapsed after snapshot A, capped when extrapolating. */
  private since(): number {
    const { a, b, t, ahead } = this.bracket;
    if (!a) return 0;
    return b ? t * (b.roomTick - a.roomTick) : Math.min(ahead, INTERPOLATION.maxExtrapolateTicks);
  }

  /** Writes the interpolated sim fields of `slot` into `out`; false if the bean is not active at A. */
  sampleBean(slot: number, out: BeanView): boolean {
    const { a, b, t } = this.bracket;
    const rec = a?.beans[slot];
    if (!rec) return false;
    sampleBean(out, rec, b ? b.beans[slot] : null, t, this.since());
    return true;
  }

  /**
   * Writes A's entities into `out` (resized to their count) and returns that count. Entities that
   * are gone at B stay visible, extrapolated, until the render tick reaches B.
   */
  sampleEntities(out: EntityView[]): number {
    const { a, b, t } = this.bracket;
    const n = a ? a.ents.length : 0;
    resizeEntityViews(out, n);
    if (!a) return 0;
    const since = this.since();
    for (let i = 0; i < n; i++) {
      const ea = a.ents[i];
      sampleEntity(out[i], ea, b ? findEnt(b.ents, ea.id) : null, t, since);
    }
    return n;
  }
}
